import React, { useState } from 'react';
import axios from 'axios';
import './SellerContact.css';
import { AppHeader, BottomNavigation, CreateOptionsMenu, BackButton, Button } from './shared';
import { useLogout, useUserData } from '../hooks';
import { useNavigation } from '../contexts/NavigationContext';
import { useLanguage } from '../contexts/LanguageContext';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';

function SellerContact({ marketplaceObject }) {
  const navigation = useNavigation();
  const { userData } = useUserData();
  const { handleLogout, isLoggingOut, logoutError, clearLogoutError } = useLogout(navigation.onLogout);
  const { t } = useLanguage();

  const [message, setMessage] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState(null);
  const [showCreateOptions, setShowCreateOptions] = useState(false);

  const handleSendMessage = async () => {
    if (!message.trim()) {
      setError(t('sellerContact.enterMessage'));
      return;
    }

    setIsSending(true);
    setError(null);

    try {
      const response = await axios.post(`${API_BASE_URL}/api/conversations`, {
        marketplace_object_id: marketplaceObject.id,
        message: message.trim()
      });

      if (response.data.success && response.data.conversation_id) {
        // Navigate to chat page with conversation ID
        // Issue #430: flag this chat as opened from the contact-seller page so
        // its "back" button returns to the chat list, not this contact page.
        navigation.onNavigateToChat(response.data.conversation_id, true);
      }
    } catch (err) {
      console.error('Failed to send message:', err);
      setError(err.response?.data?.message || t('sellerContact.sendError'));
    } finally {
      setIsSending(false);
    }
  };

  if (!marketplaceObject) {
    return <div>{t('sellerContact.objectNotFound')}</div>;
  }

  // Get seller's first name
  const sellerFirstName = marketplaceObject.author_name?.split(' ')[0] || t('sellerContact.seller');

  return (
    <div className="seller-contact-page">
      {/* Header Navigation Bar - Using shared AppHeader component */}
      <AppHeader
        userData={userData}
        onLogout={handleLogout}
        isLoggingOut={isLoggingOut}
        logoutError={logoutError}
        onClearLogoutError={clearLogoutError}
      />

      {/* Tab Navigation */}
      <div className="tab-navigation-contact">
        <span className="tab-title">{t('sellerContact.title')}</span>
      </div>

      {/* Content */}
      <main className="seller-contact-content">
        {/* Back Button */}
        <BackButton onClick={navigation.onNavigateBack} />

        {/* Seller Info Section */}
        <div className="seller-info-section">
          <span className="seller-name">{sellerFirstName}</span>
          <span className="object-title-contact">{marketplaceObject.title}</span>
          <span className="object-price-contact">
            {marketplaceObject.is_free_giveaway ? t('marketplace.freeGiveaway') : `${marketplaceObject.price} €`}
          </span>
        </div>

        {/* Message Input Section */}
        <div className="message-section">
          <label className="message-label">{t('sellerContact.message')}</label>
          <textarea
            className="message-input"
            placeholder={t('sellerContact.messagePlaceholder')}
            value={message}
            onChange={(e) => {
              setMessage(e.target.value);
              e.target.style.height = 'auto';
              e.target.style.height = e.target.scrollHeight + 'px';
            }}
            maxLength={2000}
            style={{ overflow: 'hidden', resize: 'none', minHeight: '100px', height: 'auto' }}
          />
        </div>

        {/* GDPR Notice */}
        <div className="gdpr-notice">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
            <line x1="12" y1="16" x2="12" y2="12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            <circle cx="12" cy="8" r="1" fill="currentColor"/>
          </svg>
          <span>{t('sellerContact.gdprNotice')}</span>
        </div>

        {/* Error Message */}
        {error && (
          <div className="error-message">
            {error}
          </div>
        )}

        {/* Send Button */}
        <Button
          variant="primary"
          className="send-message-button-contact"
          onClick={handleSendMessage}
          disabled={isSending || !message.trim()}
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M21 11.5C21.0034 12.8199 20.6951 14.1219 20.1 15.3C19.3944 16.7118 18.3098 17.8992 16.9674 18.7293C15.6251 19.5594 14.0782 19.9994 12.5 20C11.1801 20.0035 9.87812 19.6951 8.7 19.1L3 21L4.9 15.3C4.30493 14.1219 3.99656 12.8199 4 11.5C4.00061 9.92179 4.44061 8.37488 5.27072 7.03258C6.10083 5.69028 7.28825 4.6056 8.7 3.90003C9.87812 3.30496 11.1801 2.99659 12.5 3.00003H13C15.0843 3.11502 17.053 3.99479 18.5291 5.47089C20.0052 6.94699 20.885 8.91568 21 11V11.5Z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          <span>{isSending ? t('sellerContact.sending') : t('sellerContact.send')}</span>
        </Button>
      </main>

      {/* Bottom Navigation Bar - Using shared component */}
      <BottomNavigation
        activeTab="messages"
        onCreateClick={() => setShowCreateOptions(!showCreateOptions)}
      />

      {/* Create Options Menu - Using shared component */}
      <CreateOptionsMenu
        isOpen={showCreateOptions}
        onClose={() => setShowCreateOptions(false)}
      />
    </div>
  );
}

export default SellerContact;
